/**
 * @file CarrierSyncLogList.tsx
 * @description Журнал последних синхронизаций кэша ПВЗ (phase19.12).
 *
 * На мобильном (<sm) каждая запись — компактная карточка в столбик, на
 * десктопе та же запись схлопывается в одну строку.
 */

import { AlertTriangle, Check, Loader2 } from "lucide-react";

import type { CarrierSyncLogEntry } from "../../lib/api-shop-carriers";

interface Props {
  entries: CarrierSyncLogEntry[];
  loading: boolean;
  error: string | null;
}

/** «14.08 10:25» — год не нужен, журнал короткий. */
function formatStartedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Длительность в секундах, с одним знаком для быстрых синков. */
function formatDuration(ms: number | null): string | null {
  if (ms === null) return null;
  const sec = ms / 1000;
  return `${sec < 10 ? sec.toFixed(1) : Math.round(sec)} с`;
}

export function CarrierSyncLogList({ entries, loading, error }: Props) {
  if (loading) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-on-surface-variant">
        <Loader2 className="size-3.5 animate-spin" /> Загружаем журнал…
      </p>
    );
  }
  if (error) {
    return <p className="text-xs text-on-surface-variant">{error}</p>;
  }
  if (entries.length === 0) {
    return <p className="text-xs text-on-surface-variant">Синхронизаций ещё не было.</p>;
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {entries.map((e) => {
        const duration = formatDuration(e.durationMs);
        return (
          <li
            key={e.id}
            className="flex flex-col gap-0.5 rounded-md border border-on-surface/10 p-2 text-xs sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:border-0 sm:px-0 sm:py-1"
          >
            <span className="font-medium tabular-nums">{formatStartedAt(e.startedAt)}</span>
            <span className="text-on-surface-variant">
              {e.trigger === "MANUAL" ? "вручную" : "авто"}
            </span>
            <span className="tabular-nums text-on-surface-variant">
              +{e.added} нов. / изм. {e.updated} / откл. {e.deactivated}
            </span>
            {e.status !== "RUNNING" && (
              <span className="tabular-nums text-on-surface-variant">
                всего {e.totalAfter.toLocaleString("ru-RU")}
                {duration ? ` · ${duration}` : ""}
              </span>
            )}
            <span className="flex items-center gap-1">
              {e.status === "RUNNING" && (
                <>
                  <Loader2 className="size-3.5 animate-spin" /> выполняется…
                </>
              )}
              {e.status === "SUCCESS" && (
                <>
                  <Check className="size-3.5 text-primary" /> ОК
                </>
              )}
              {e.status === "ERROR" && (
                <span className="flex items-center gap-1 text-error">
                  <AlertTriangle className="size-3.5" /> ошибка
                </span>
              )}
            </span>
            {e.status === "ERROR" && e.errorMessage && (
              <p className="w-full text-error sm:pl-0">{e.errorMessage}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
